import { useEffect, useState } from 'react';
import { Heading } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { plainDate, WATCHOUT_SEVERITIES, type PlainDate, type WatchoutSeverity } from '@complan/domain';
import type { CreateWatchoutRequest, Finding, WatchoutDto } from '@complan/contracts';

import { AriaModalDialog } from '@/shared/a11y/AriaDialog';
import { Button } from '@/components/ui/button';
import { DateField } from '@/shared/components/DateField';
import { FindingsAlert } from '@/features/admin/FindingsAlert';
import { TextArea } from '@/features/initiative/intake/fields';

/** The longest description migration 26's `watchout_description_length` admits (R7.1). */
const DESCRIPTION_MAX_LENGTH = 1000;

/** One selectable Watchout category, from the master-data read. */
export interface WatchoutCategoryOption {
  readonly id: string;
  readonly name: string;
}

/**
 * The add/edit Watchout dialog (`complan-governance` R7.1, R7.4).
 *
 * One dialog for both operations, because R7.4 makes add and edit the same shape: a
 * category, a description, a severity, an optional owner and an optional due date. Two
 * dialogs would be two copies of that form and two places for the description bound to
 * drift from migration 26's constraint.
 *
 * ## Findings, not a boolean
 *
 * The other Initiative dialogs take `hasError: boolean`, which is enough for them because
 * their failures are all one shape. A Watchout write can be rejected by the server for
 * reasons the reader must act on and can only be told by the server - a deactivated
 * category, a description that violates the stored length rule, an owner who no longer
 * exists. So this dialog renders {@link FindingsAlert}, the same component the master-data
 * editor uses, and never composes a message of its own about a rule it does not own.
 *
 * ## Why the category list is required to be non-empty
 *
 * A Watchout must carry a category (R7.1), and categories are master data (R7.3). With
 * none configured there is nothing valid to submit, so the dialog says so rather than
 * offering an empty select that would fail on submit.
 */
export interface WatchoutDialogProps {
  readonly isOpen: boolean;
  /** The Watchout being edited, or `null` to add a new one. */
  readonly watchout: WatchoutDto | null;
  readonly categories: readonly WatchoutCategoryOption[];
  readonly isSubmitting: boolean;
  readonly findings: readonly Finding[];
  readonly onConfirm: (request: CreateWatchoutRequest) => void;
  readonly onClose: () => void;
}

export function WatchoutDialog({
  isOpen,
  watchout,
  categories,
  isSubmitting,
  findings,
  onConfirm,
  onClose,
}: WatchoutDialogProps): React.JSX.Element {
  const { t } = useTranslation();

  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<WatchoutSeverity>('medium');
  const [dueDate, setDueDate] = useState<PlainDate | null>(null);

  // Re-seeded whenever the dialog opens or the target changes, so editing one Watchout
  // never shows another's values and adding never inherits the last edit. The same
  // seed-on-open discipline `RecordEditorDialog` uses, for the same reason.
  const seedKey = `${watchout?.id ?? 'new'}:${String(isOpen)}`;
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setCategoryId(watchout?.watchoutCategoryId ?? categories[0]?.id ?? '');
    setDescription(watchout?.description ?? '');
    setSeverity(watchout?.severity ?? 'medium');
    setDueDate(watchout?.dueDate ?? null);
    // `seedKey` is deliberately the *only* dependency. The effect must re-seed when the
    // dialog opens or the target changes, and must not re-run for anything else: listing
    // `watchout` or `categories` would make it fire whenever either object's identity
    // changed - which a background refetch of the watchout list does - and wipe whatever
    // the reader had typed. The repo's ESLint config carries no
    // `react-hooks/exhaustive-deps` rule, so this is a stated decision rather than a
    // suppressed warning.
  }, [seedKey]);

  const trimmed = description.trim();
  const hasCategories = categories.length > 0;
  const isValid =
    hasCategories &&
    categoryId !== '' &&
    trimmed.length >= 1 &&
    trimmed.length <= DESCRIPTION_MAX_LENGTH;

  return (
    <AriaModalDialog
      variant="centre"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      aria-label={watchout === null ? t('watchout.addDialog.title') : t('watchout.editDialog.title')}
    >
      <Heading slot="title" className="text-txt text-[15px] font-semibold">
        {watchout === null ? t('watchout.addDialog.title') : t('watchout.editDialog.title')}
      </Heading>

      <FindingsAlert findings={findings} />

      {hasCategories ? null : (
        <p className="mt-2 text-[12px] text-[var(--warn-txt,#7d5300)]">
          {t('watchout.dialog.noCategories')}
        </p>
      )}

      <div className="mt-3 flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-txt text-[11.5px] font-semibold">
            {t('watchout.field.category')}
          </span>
          <select
            className="border-line bg-panel text-txt h-8 rounded-[var(--radius-control)] border px-2 text-[12.5px]"
            value={categoryId}
            disabled={!hasCategories}
            onChange={(event) => setCategoryId(event.target.value)}
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-txt text-[11.5px] font-semibold">
            {t('watchout.field.severity')}
          </span>
          <select
            className="border-line bg-panel text-txt h-8 rounded-[var(--radius-control)] border px-2 text-[12.5px]"
            value={severity}
            onChange={(event) => setSeverity(event.target.value as WatchoutSeverity)}
          >
            {/* Highest first: a reader raising a concern is most often raising a
                serious one, and the domain's own order is ascending. */}
            {[...WATCHOUT_SEVERITIES].reverse().map((option) => (
              <option key={option} value={option}>
                {t(`watchoutSeverity.${option}`)}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-col gap-1">
          <label htmlFor="watchout-description" className="text-txt text-[11.5px] font-semibold">
            {t('watchout.field.description')}
          </label>
          <TextArea
            id="watchout-description"
            value={description}
            maxLength={DESCRIPTION_MAX_LENGTH}
            onChange={(event) => setDescription(event.target.value)}
          />
          <p className="text-muted-foreground text-[11px]">{t('watchout.field.descriptionHint')}</p>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-txt text-[11.5px] font-semibold">{t('watchout.field.dueDate')}</span>
          <DateField
            label={t('watchout.field.dueDate')}
            value={dueDate}
            // `DateField` speaks ISO strings; the contract wants the branded date, and
            // `plainDate` is the one place that validates the shape.
            onChange={(value) => setDueDate(value === null ? null : plainDate(value))}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
          {t('watchout.dialog.cancel')}
        </Button>
        <Button
          size="sm"
          disabled={!isValid || isSubmitting}
          onClick={() => {
            onConfirm({
              watchoutCategoryId: categoryId,
              description: trimmed,
              severity,
              // The owner is deliberately not offered here. R7.1 makes it optional, and
              // assigning one needs the selectable-user read the intake form uses; adding
              // a half-populated picker would be worse than leaving the field unset, which
              // the server accepts.
              ownerUserId: null,
              dueDate,
            });
          }}
        >
          {isSubmitting ? t('watchout.dialog.saving') : t('watchout.dialog.save')}
        </Button>
      </div>
    </AriaModalDialog>
  );
}

/**
 * The resolve dialog (R7.8).
 *
 * Separate from the add/edit form because resolving is a different act: it records a
 * timestamp and an actor on the retained row and takes only an optional note. Folding it
 * into the editor would offer a reader editing a concern the chance to close it by
 * accident.
 */
export function ResolveWatchoutDialog({
  isOpen,
  isSubmitting,
  findings,
  onConfirm,
  onClose,
}: {
  readonly isOpen: boolean;
  readonly isSubmitting: boolean;
  readonly findings: readonly Finding[];
  readonly onConfirm: (resolutionNote: string | null) => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [note, setNote] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNote('');
    }
  }, [isOpen]);

  const trimmed = note.trim();

  return (
    <AriaModalDialog
      variant="centre"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      aria-label={t('watchout.resolveDialog.title')}
    >
      <Heading slot="title" className="text-txt text-[15px] font-semibold">
        {t('watchout.resolveDialog.title')}
      </Heading>
      {/* R7.8: the concern is retained, not deleted. Said plainly, because "resolve"
          could reasonably be read as "remove". */}
      <p className="text-muted-foreground mt-2 text-[12.3px]">{t('watchout.resolveDialog.body')}</p>

      <FindingsAlert findings={findings} />

      <div className="mt-3 flex flex-col gap-1">
        <label htmlFor="watchout-resolution" className="text-txt text-[11.5px] font-semibold">
          {t('watchout.field.resolutionNote')}
        </label>
        <TextArea
          id="watchout-resolution"
          value={note}
          maxLength={DESCRIPTION_MAX_LENGTH}
          onChange={(event) => setNote(event.target.value)}
        />
        <p className="text-muted-foreground text-[11px]">
          {t('watchout.field.resolutionNoteHint')}
        </p>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
          {t('watchout.dialog.cancel')}
        </Button>
        <Button
          size="sm"
          disabled={isSubmitting}
          onClick={() => {
            // Empty is `null`, not `''`: the schema rejects a blank note, and an empty box
            // means "no note" rather than "a note with nothing in it".
            onConfirm(trimmed === '' ? null : trimmed);
          }}
        >
          {isSubmitting ? t('watchout.dialog.resolving') : t('watchout.dialog.resolve')}
        </Button>
      </div>
    </AriaModalDialog>
  );
}
